export interface FittedRect {
  x: number;      // offset from left of container, in pixels
  y: number;      // offset from top of container, in pixels
  width: number;
  height: number;
}

/**
 * Client-side equivalent of the server's containImageInBox.
 * Computes the exact position and size an image should have inside a container
 * to match `object-fit: contain` + centered, using the same math as the PDF server.
 *
 * This is the proper way to achieve reliable WYSIWYG for signature images:
 * instead of trusting CSS flex + object-fit for final positioning, we compute
 * the rect explicitly on both sides with identical logic.
 */
export function containImageInBoxClient(
  imgW: number,   // intrinsic width of the image (pixels for client, points for server)
  imgH: number,
  boxW: number,   // container width in same units
  boxH: number,
): FittedRect {
  if (imgW <= 0 || imgH <= 0 || boxW <= 0 || boxH <= 0) {
    return { x: 0, y: 0, width: boxW, height: boxH };
  }

  const scale = Math.min(boxW / imgW, boxH / imgH);
  const width = imgW * scale;
  const height = imgH * scale;

  return {
    x: (boxW - width) / 2,
    y: (boxH - height) / 2,
    width,
    height,
  };
}
